import * as React from 'react';
import moment from 'moment';

import ActionButton from 'components/buttons/action-button/ActionButton';
import { withModal } from 'components/modal/ModalHOC';
import StatusLabel from 'components/status-label/StatusLabel';
import Spinner from 'components/spinner/Spinner';
import CampaignPayoutsInstallmentsModal from '../modals/installments/CampaignPayoutsInstallmentsModal';
import CampaignPayoutsReleaseModal from '../release/CampaignPayoutsReleaseModal';
import CampaignPayoutsHeaderInfo from '../header-info/CampaignPayoutsHeaderInfo';
import CampaignPayoutsHeaderVariable from './variable/CampaignPayoutsHeaderVariable';

import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import { isActive, isInactive } from 'utils/campaign';
import { isInfluencerProfileRejected } from 'utils/profile';
import {
  canCreatePayouts,
  getAcceptedOfferBudget,
  getExpectedPayoutDateWithRelationToReleaseStatus,
  getTotalVariablePayment,
  isVeryOldCampaignForPayouts
} from 'utils/payouts';

import './campaign-payouts-header.scss';

import type {
  Campaign,
  PaymentCampaignBudget,
  PaymentInfo,
  PaymentsLoading,
  Subcampaign,
  VariablePayment
} from 'types';

applyTranslations(require('./campaign-payouts-header.i18n.json'));

type Props = {
  campaign: Campaign,
  subcampaign: Subcampaign,
  campaignBudgetInfo: PaymentCampaignBudget,
  paymentInfo: PaymentInfo,
  unallocatedAmount: number,
  variablePayments: Array<VariablePayment>,
  hasPayoutInstallments: boolean,
  hasUnpaidPayoutInstallments: boolean,
  isTotalPayout: boolean,
  totalPayoutExecuted: boolean | undefined | null,
  isNegotiationReopened: boolean,
  releaseDate: string | undefined | null,
  originalReleaseDate: string | undefined | null,
  otherArrangement: boolean,
  isLoading: PaymentsLoading,
  onCreateVariablePayments: (b: number, a: Array<VariablePayment>) => void,
  onEditVariablePayments: (b: number, a: Array<VariablePayment>) => void
};

const ActionButtonWithModalRelease = withModal(ActionButton, {
  modalContent: CampaignPayoutsReleaseModal,
  width: 'small'
});

const ActionButtonWithModalInstallments = withModal(ActionButton, {
  modalContent: CampaignPayoutsInstallmentsModal
});

class CampaignPayoutsHeader extends React.PureComponent<Props> {
  showPayoutReleased(): boolean {
    const { campaign, isTotalPayout, unallocatedAmount, hasUnpaidPayoutInstallments } = this.props;

    return (!this.showReleaseButton() && !this.showCreateButton())
      && !hasUnpaidPayoutInstallments
      && (
        isVeryOldCampaignForPayouts(campaign)
        || (unallocatedAmount <= 0 && isTotalPayout)
      );
  }

  showReleaseButton(): boolean {
    const { campaign, paymentInfo, hasPayoutInstallments } = this.props;

    return (isActive(campaign) || isInactive(campaign))
      && paymentInfo.amounts.budget > 0
      && !hasPayoutInstallments;
  }

  showCreateButton(): boolean {
    const { campaign, hasPayoutInstallments } = this.props;

    return this.showReleaseButton()
      && canCreatePayouts(campaign)
      && !isVeryOldCampaignForPayouts(campaign)
      && !hasPayoutInstallments;
  }

  showEditButton(): boolean {
    const { campaign, paymentInfo, otherArrangement, unallocatedAmount, hasUnpaidPayoutInstallments } = this.props;
    const variablePayment = getTotalVariablePayment(paymentInfo);

    return !this.showReleaseButton()
      && !this.showCreateButton()
      && !this.showPayoutReleased()
      && !isInactive(campaign)
      && (
        unallocatedAmount > 0
        || hasUnpaidPayoutInstallments
        || (otherArrangement && variablePayment > 0)
      );
  }

  showMessageButton(): boolean {
    const { paymentInfo, subcampaign, isTotalPayout, otherArrangement, hasPayoutInstallments } = this.props;
    const variablePayment = getTotalVariablePayment(paymentInfo);
    const isInfluencerRejected = isInfluencerProfileRejected(subcampaign.creatorProfile);

    return !isInfluencerRejected && ((
      !this.showReleaseButton()
      && !this.showCreateButton()
      && !this.showPayoutReleased()
    ) || (
      this.showEditButton()
      && (
        otherArrangement
        || isTotalPayout
        || !hasPayoutInstallments
        || (!isTotalPayout && !variablePayment)
      )
    ));
  }

  showButton(type: 'release' | 'create' | 'edit' | 'message'): boolean {
    if (type === 'release') {
      return this.showReleaseButton();
    }

    if (type === 'create') {
      return this.showCreateButton();
    }

    if (type === 'edit') {
      return this.showEditButton();
    }

    if (type === 'message') {
      return this.showMessageButton();
    }

    return false;
  }

  renderSpinner(): React.ReactNode {
    return (
      <div className="campaign-payouts-header__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderActionButtonWithModalRelease(): React.ReactNode {
    const { campaign, subcampaign, paymentInfo, isNegotiationReopened } = this.props;
    const variablePayment = getTotalVariablePayment(paymentInfo);

    return (
      <ActionButtonWithModalRelease
        modalContentProps={{
          influencerProfile: subcampaign.creatorProfile,
          campaignId: campaign.id,
          subcampaignId: subcampaign.id,
          totalBudget: getAcceptedOfferBudget(subcampaign) + variablePayment,
          isNegotiationReopened
        }}
      >
        {__t('campaign_payouts_header.buttons.release_total_payout')}
      </ActionButtonWithModalRelease>
    );
  }

  renderButtonReleaseTotalPayout(): React.ReactNode | undefined | null {
    if (!this.showButton('release')) {
      return null;
    }

    return (
      <div className="campaign-payouts-header__button">
        {this.renderActionButtonWithModalRelease()}
      </div>
    );
  }

  renderButtonCreatePayoutInstallments(): React.ReactNode | undefined | null {
    const { campaign, subcampaign, paymentInfo } = this.props;
    const variablePayment = getTotalVariablePayment(paymentInfo);

    if (!this.showButton('create')) {
      return null;
    }

    return (
      <div className="campaign-payouts-header__button">
        <ActionButtonWithModalInstallments
          modalContentProps={{
            influencerProfile: subcampaign.creatorProfile,
            campaignId: campaign.id,
            subcampaignId: subcampaign.id,
            totalBudget: getAcceptedOfferBudget(subcampaign) + variablePayment,
            campaignEndDatetime: campaign.endDate
          }}
        >
          {__t('campaign_payouts_header.buttons.create_payout_installments')}
        </ActionButtonWithModalInstallments>
      </div>
    );
  }

  renderButtonEditInstallments(): React.ReactNode | undefined | null {
    const { campaign, subcampaign, paymentInfo } = this.props;
    const variablePayment = getTotalVariablePayment(paymentInfo);

    if (!this.showButton('edit')) {
      return null;
    }

    return (
      <div className="campaign-payouts-header__button">
        <ActionButtonWithModalInstallments
          modalContentProps={{
            influencerProfile: subcampaign.creatorProfile,
            campaignId: campaign.id,
            subcampaignId: subcampaign.id,
            totalBudget: getAcceptedOfferBudget(subcampaign) + variablePayment,
            campaignEndDatetime: campaign.endDate
          }}
        >
          {__t('campaign_payouts_header.buttons.edit_payout_installments')}
        </ActionButtonWithModalInstallments>
      </div>
    );
  }

  renderButtonMessageCreator(): React.ReactNode | undefined | null {
    const { campaign, subcampaign } = this.props;

    if (!this.showButton('message')) {
      return null;
    }

    return (
      <div className="campaign-payouts-header__button">
        <ActionButton
          link={`/campaigns/${campaign.id}/messages?subcampaignId=${subcampaign.id}`}
        >
          {__t('campaign_payouts_header.buttons.message_creator')}
        </ActionButton>
      </div>
    );
  }

  renderPayoutReleased = (): React.ReactNode | undefined | null => {
    const {
      campaign,
      releaseDate,
      originalReleaseDate,
      totalPayoutExecuted
    } = this.props;

    if (!this.showPayoutReleased()) {
      return null;
    }

    const expectedPayoutDate = releaseDate
      ? getExpectedPayoutDateWithRelationToReleaseStatus(new Date(), releaseDate, true)
      : getExpectedPayoutDateWithRelationToReleaseStatus(new Date(), campaign.endDate);

    const today = moment(new Date());
    const isExecuted = totalPayoutExecuted
      || (expectedPayoutDate && today.isAfter(expectedPayoutDate));

    return (
      <div className="campaign-payouts-header__total-payout">
        <div className="campaign-payouts-header__total-payout-released">
          <div>
            <span className="campaign-payouts-header__total-payout-released-label">
              {__t('campaign_payouts_header.labels.payout_released')}:
            </span>
            &nbsp;{getDisplayDate(originalReleaseDate)}
          </div>
          <div>
            <span className="campaign-payouts-header__total-payout-released-label">
              {__t('campaign_payouts_header.labels.payout_expected')}:
            </span>
            &nbsp;{getDisplayDate(expectedPayoutDate)}
          </div>
        </div>
        {
          isExecuted && (
            <div className="campaign-payouts-header__total-payout-status">
              <StatusLabel
                label={__t('campaign_payouts_header.labels.paid')}
                size="small"
                data={{ qa: 'campaign-payouts-header__total-payout--label' }}
              />
            </div>
          )
        }
      </div>
    );
  };

  renderResidual(): React.ReactNode | undefined | null {
    return (
      <div className="campaign-payouts-header__buttons">
        {this.renderButtonEditInstallments()}
        {this.renderButtonReleaseTotalPayout()}
        {this.renderButtonCreatePayoutInstallments()}
        {this.renderButtonMessageCreator()}
        {this.renderPayoutReleased()}
      </div>
    );
  }

  renderContent(): React.ReactNode {
    const {
      campaign,
      subcampaign,
      campaignBudgetInfo,
      paymentInfo,
      variablePayments,
      otherArrangement,
      isLoading,
      onCreateVariablePayments,
      onEditVariablePayments
    } = this.props;

    return (
      <React.Fragment>
        <CampaignPayoutsHeaderInfo
          profile={subcampaign.creatorProfile}
          paymentInfo={paymentInfo}
          otherArrangement={otherArrangement}
          isBrand
        />
        <div className="subcampaign-payouts-header__offer">
          <CampaignPayoutsHeaderVariable
            campaignName={campaign.name}
            subcampaignId={subcampaign.id}
            paymentInfo={paymentInfo}
            variablePayments={variablePayments}
            influencerProfile={subcampaign.creatorProfile}
            campaignBudgetInfo={campaignBudgetInfo}
            otherArrangement={otherArrangement}
            isLoading={isLoading.variablePayments}
            onCreateVariablePayments={onCreateVariablePayments}
            onEditVariablePayments={onEditVariablePayments}
          />
        </div>
        {this.renderResidual()}
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { isLoading } = this.props;

    return (
      <section
        className="campaign-payouts-header"
        data-qa="campaign-payouts-header"
      >
        {
          isLoading.payouts
            ? this.renderSpinner()
            : this.renderContent()
        }
      </section>
    );
  }
}

export default CampaignPayoutsHeader;
